import { useState, useEffect, useCallback } from "react";
import { X, History, UserPlus, Pencil, Trash2, User, Filter, RotateCw, ChevronDown, ChevronRight, Printer, Layers } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BraveSelect } from "@/components/ui/brave-select";
import clienteApi from "@/lib/clienteApi";
import ImprimirListaModal from "@/components/ImprimirListaModal";

/**
 * Modal de Auditoria de Cadastros.
 *
 * Props:
 *   open              boolean
 *   onOpenChange      (open) => void
 *   modulo            'moradores' | 'visitantes' | 'prestadores'
 *   modo              'registro' (histórico de 1 cadastro) | 'relatorio' (grid com filtros)
 *   registroId        required se modo='registro'
 *   titulo            (opcional) título custom — default depende do modulo/modo
 *   subtitulo         (opcional)
 *   limit             (apenas modo 'registro') default 50
 */
function formatOp(op) {
  switch (op) {
    case "incluir": return { label: "Inclusão", cls: "bg-emerald-500/20 text-emerald-300 border-emerald-400/30", Icon: UserPlus };
    case "alterar": return { label: "Alteração", cls: "bg-sky-500/20 text-sky-300 border-sky-400/30", Icon: Pencil };
    case "excluir": return { label: "Exclusão", cls: "bg-red-500/20 text-red-300 border-red-400/30", Icon: Trash2 };
    default: return { label: op || "—", cls: "bg-gray-500/20 text-gray-300 border-gray-400/30", Icon: History };
  }
}

// Mapeamento de nomes de campos técnicos → labels amigáveis
const FIELD_LABELS = {
  nome: "Nome",
  matricula: "Matrícula",
  cpf: "CPF",
  rg: "RG",
  ativo: "Ativo",
  telefone: "Telefone",
  celular: "Celular",
  email: "E-mail",
  cnpj: "CNPJ",
  razao_social: "Razão Social",
  codigo: "Código",
  codigo_rfid: "Código RFID",
  codigo_barras: "Código de Barras",
  horario: "Horário",
  tipo_cartao: "Tipo de Cartão",
  tipo_propriedade: "Tipo de Propriedade",
  classificacao: "Classificação",
  departamento: "Setor",
  departamento_id: "Setor",
  funcao: "Função",
  funcao_id: "Função",
  parentesco: "Parentesco",
  local_acesso_id: "Local de Acesso",
  observacao: "Observação",
  observacoes: "Observações",
  endereco: "Endereço",
  logradouro: "Logradouro",
  numero: "Número",
  complemento: "Complemento",
  bairro: "Bairro",
  cidade: "Cidade",
  uf: "UF",
  cep: "CEP",
  atividade: "Atividade",
  data_nascimento: "Data de Nascimento",
  sexo: "Sexo",
  validade: "Validade",
  tipo_cadastro: "Tipo de Cadastro",
  placa: "Placa",
  modelo: "Modelo",
  modelo_veiculo: "Modelo do Veículo",
  cor: "Cor",
  cor_veiculo: "Cor do Veículo",
  vaga: "Vaga",
  veiculos: "Veículos",
  foto_url: "Foto",
  foto_base64: "Foto",
  // Visitantes — campos do modal de Observações e Saídas
  observacao_cadastro: "Observação do Cadastro",
  observacao_cpf: "Observação do CPF",
  data_hora_entrada: "Data/Hora de Entrada",
  data_hora_saida: "Data/Hora de Saída",
  data_hora_autorizou: "Data/Hora da Autorização",
  data_cadastro: "Data do Cadastro",
  data_agendamento: "Data do Agendamento",
  acesso_aberto: "Acesso em Aberto",
  emespera: "Em Espera",
  monitorado: "Monitorado",
  tempo_limite: "Tempo Limite",
  quemautorizou: "Quem Autorizou",
  motivo: "Motivo",
  empresa_nome: "Empresa",
  pessoa_visitada_nome: "Pessoa Visitada",
  local_acesso_nome: "Local de Acesso",
  unidade: "EQL",
  bloco: "Bloco",
  predio: "Prédio",
  andar: "Andar",
  ddi_celular: "DDI Celular",
  qrcode: "QRCode",
  vip: "VIP",
  lgpd: "LGPD",
  mais_info: "Mais Informações",
};

// Campos cujos valores devem ser formatados como data/hora
const DATE_FIELDS = new Set([
  "data_hora_entrada",
  "data_hora_saida",
  "data_hora_autorizou",
  "data_cadastro",
  "data_agendamento",
  "tempo_limite",
  "data_nascimento",
  "validade",
]);

function fieldLabel(k) {
  return FIELD_LABELS[k] || k.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
}

function formatFieldValue(v, fieldKey) {
  if (v === null || v === undefined) return <span className="text-brand-ice/30 italic">vazio</span>;
  if (v === "") return <span className="text-brand-ice/30 italic">vazio</span>;
  if (typeof v === "boolean") return v ? "Sim" : "Não";
  if (Array.isArray(v)) return <span className="text-brand-ice/70 italic">{v.length} item(s)</span>;
  if (typeof v === "object") return <span className="text-brand-ice/70 italic">objeto</span>;
  // Campos de data/hora — formatar amigável
  if (fieldKey && DATE_FIELDS.has(fieldKey) && typeof v === "string" && v.includes("T")) {
    return formatDataLocal(v);
  }
  const s = String(v);
  if (s.length > 80) return s.slice(0, 80) + "…";
  return s;
}

function formatDataLocal(v) {
  if (!v) return "—";
  // v = "YYYY-MM-DDTHH:mm:ss" já no fuso local do cliente
  try {
    const [d, t] = v.split("T");
    const [y, mo, dd] = d.split("-");
    const [hh, mm, ss] = (t || "00:00:00").split(":");
    return `${dd}/${mo}/${y} ${hh}:${mm}:${ss}`;
  } catch {
    return v;
  }
}

// Retorna YYYY-MM-DD no fuso informado (ou local do navegador se vazio).
// Evita o bug de `toISOString()` que retorna UTC e adianta o dia para
// operadores em UTC-3 (Brasília) próximos à meia-noite.
const HOJE = (tz) => {
  try {
    return new Date().toLocaleDateString("en-CA", tz ? { timeZone: tz } : undefined);
  } catch {
    return new Date().toLocaleDateString("en-CA");
  }
};

function AuditRow({ item, idx, modo }) {
  const [open, setOpen] = useState(false);
  const op = formatOp(item.operacao);
  const mudancas = item?.extra?.mudancas || {};
  const hasMudancas = Object.keys(mudancas).length > 0;
  const cascata = item?.extra?.cascata_inativacao || null;
  const hasCascata = !!(cascata && Array.isArray(cascata.eqls) && cascata.eqls.length > 0);
  const expandable = hasMudancas || hasCascata;

  return (
    <>
      <tr
        data-testid={`audit-row-${idx}`}
        className="border-b border-brand-ice/10 hover:bg-white/5 transition-colors"
      >
        <td className="py-2.5 px-2 w-8">
          {expandable ? (
            <button
              data-testid={`audit-expand-${idx}`}
              onClick={() => setOpen((o) => !o)}
              title={open ? "Ocultar detalhes" : "Ver detalhes"}
              className="p-1 rounded hover:bg-white/10 text-brand-light transition-colors"
            >
              {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
          ) : (
            <span className="inline-block w-6" />
          )}
        </td>
        <td className="py-2.5 px-3 text-brand-ice/90 font-mono text-xs whitespace-nowrap">
          {formatDataLocal(item.data_hora_local)}
        </td>
        <td className="py-2.5 px-3">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs font-medium ${op.cls}`}>
            <op.Icon className="w-3 h-3" />
            {op.label}
          </span>
          {hasCascata && (
            <span
              data-testid={`audit-cascata-badge-${idx}`}
              title={`Inativação em cascata — ${cascata.total_lotes} lote(s)`}
              className="ml-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-bold bg-amber-500/20 text-amber-300 border-amber-400/30"
            >
              <Layers className="w-3 h-3" />
              {cascata.total_lotes} lotes
            </span>
          )}
        </td>
        <td className="py-2.5 px-3 text-brand-ice">
          <span className="inline-flex items-center gap-1.5">
            <User className="w-3 h-3 text-brand-ice/50" />
            <span className="font-mono text-xs text-brand-ice/60">{item.operador_codigo}</span>
            <span>{item.operador_nome}</span>
          </span>
        </td>
        {modo === "relatorio" && (
          <>
            <td className="py-2.5 px-3 text-brand-ice font-mono text-xs">{item.matricula ?? "—"}</td>
            <td className="py-2.5 px-3 text-brand-ice">{item.nome || "—"}</td>
          </>
        )}
      </tr>
      {open && expandable && (
        <tr data-testid={`audit-changes-${idx}`} className="bg-brand-medium/10 border-b border-brand-ice/10">
          <td colSpan={modo === "relatorio" ? 6 : 4} className="py-3 px-6">
            {hasCascata && (
              <div data-testid={`audit-cascata-${idx}`} className="mb-3 rounded-lg border border-amber-400/30 bg-amber-500/10 p-3">
                <p className="text-[10px] uppercase tracking-wider font-bold text-amber-300/90 mb-1.5 flex items-center gap-1.5">
                  <Layers className="w-3.5 h-3.5" />
                  Inativação em cascata — {cascata.total_lotes} lote(s) suspenso(s)
                </p>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {cascata.eqls.map((e, i) => (
                    <span key={`${e}-${i}`} className="font-mono text-[11px] px-2 py-0.5 rounded bg-amber-400/15 text-amber-200 border border-amber-400/20">
                      {e}
                    </span>
                  ))}
                </div>
                {cascata.motivo ? (
                  <p className="text-xs text-brand-ice/80">
                    <span className="text-brand-ice/50">Motivo:</span> {cascata.motivo}
                  </p>
                ) : (
                  <p className="text-xs text-brand-ice/40 italic">Sem motivo informado</p>
                )}
              </div>
            )}
            {hasMudancas && (
            <div className="border-l-2 border-brand-light/60 pl-4">
              <p className="text-[10px] uppercase tracking-wider font-bold text-brand-light/80 mb-2">
                Mudanças ({Object.keys(mudancas).length} {Object.keys(mudancas).length === 1 ? "campo" : "campos"})
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-brand-ice/50 text-[10px] uppercase tracking-wider">
                      <th className="text-left py-1 pr-4">Campo</th>
                      <th className="text-left py-1 pr-4">De</th>
                      <th className="text-left py-1">Para</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(mudancas).map(([k, v]) => (
                      <tr key={k} className="border-t border-brand-ice/5">
                        <td className="py-1.5 pr-4 font-medium text-brand-ice/90">{fieldLabel(k)}</td>
                        <td className="py-1.5 pr-4 text-red-300/80 line-through decoration-red-400/50">
                          {formatFieldValue(v?.de, k)}
                        </td>
                        <td className="py-1.5 text-emerald-300">
                          {formatFieldValue(v?.para, k)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

export default function AuditoriaModal({
  open,
  onOpenChange,
  modulo,
  modo = "registro",
  registroId = null,
  titulo,
  subtitulo,
  limit = 50,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [operadores, setOperadores] = useState([]);
  const [imprimirOpen, setImprimirOpen] = useState(false);

  // Fuso horário do cliente — sem ele, datas iniciais ficariam em UTC e
  // poderiam mostrar o dia seguinte em horários noturnos no Brasil.
  const [tz, setTz] = useState(null);

  // Filtros (apenas modo 'relatorio')
  const [dataInicio, setDataInicio] = useState(HOJE());
  const [dataFim, setDataFim] = useState(HOJE());
  const [operadorCodigo, setOperadorCodigo] = useState("");
  const [operacao, setOperacao] = useState("");

  const moduloLabel =
    modulo === "moradores" ? "Moradores/Funcionários"
    : modulo === "visitantes" ? "Visitantes"
    : modulo === "prestadores" ? "Prestadores"
    : modulo;

  const tituloFinal = titulo || (modo === "registro" ? "Histórico do Cadastro" : `Auditoria — ${moduloLabel}`);

  const fetchData = useCallback(async () => {
    if (!open || !modulo) return;
    setLoading(true);
    try {
      if (modo === "registro") {
        if (!registroId) { setItems([]); return; }
        const r = await clienteApi.get(`/cliente/auditoria/registro`, {
          params: { modulo, registro_id: registroId, limit },
        });
        setItems(r.data?.items || []);
      } else {
        const params = { modulo };
        if (dataInicio) params.data_inicio = dataInicio;
        if (dataFim) params.data_fim = dataFim;
        if (operadorCodigo !== "") params.operador_codigo = operadorCodigo;
        if (operacao) params.operacao = operacao;
        const r = await clienteApi.get(`/cliente/auditoria`, { params });
        setItems(r.data?.items || []);
      }
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [open, modulo, modo, registroId, limit, dataInicio, dataFim, operadorCodigo, operacao]);

  // Carregar operadores distintos (filtro)
  useEffect(() => {
    if (!open || modo !== "relatorio") return;
    clienteApi.get(`/cliente/auditoria/operadores`).then((r) => {
      setOperadores(r.data || []);
    }).catch(() => setOperadores([]));
  }, [open, modo]);

  // Carregar timezone do cliente quando o modal abrir.
  // Reajusta as datas apenas se ainda estiverem no padrão "hoje" do navegador
  // (operador ainda não interagiu com os filtros).
  useEffect(() => {
    if (!open) return;
    let cancel = false;
    (async () => {
      try {
        const r = await clienteApi.get("/cliente/configuracoes");
        if (cancel) return;
        const tzCfg = r.data?.timezone || null;
        setTz(tzCfg);
        if (tzCfg) {
          const novoHoje = HOJE(tzCfg);
          const hojeBrowser = HOJE();
          setDataInicio((cur) => (cur === hojeBrowser ? novoHoje : cur));
          setDataFim((cur) => (cur === hojeBrowser ? novoHoje : cur));
        }
      } catch {
        /* fallback silencioso para fuso local */
      }
    })();
    return () => { cancel = true; };
  }, [open]);

  useEffect(() => {
    if (open) fetchData();
  }, [open, fetchData]);

  if (!open) return null;

  const opOptions = [
    { value: "", label: "Todos" },
    { value: "incluir", label: "Inclusão" },
    { value: "alterar", label: "Alteração" },
    { value: "excluir", label: "Exclusão" },
  ];
  const operadorOptions = [
    { value: "", label: "Todos" },
    ...operadores.map((o) => ({ value: String(o.codigo ?? 0), label: `${o.codigo ?? 0} - ${o.nome || "—"}` })),
  ];

  return (
    <div
      data-testid="auditoria-modal"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={() => onOpenChange(false)}
    >
      <div
        className="relative w-full max-w-5xl max-h-[90vh] overflow-hidden rounded-2xl bg-brand-dark border border-brand-ice/15 shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-brand-ice/10">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-brand-medium/30 text-brand-light">
              <History className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-heading font-extrabold text-brand-ice text-xl">{tituloFinal}</h2>
              {subtitulo && <p className="text-brand-ice/60 text-xs mt-0.5">{subtitulo}</p>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {modo === "relatorio" && (
              <button
                data-testid="audit-imprimir-button"
                onClick={() => setImprimirOpen(true)}
                disabled={items.length === 0}
                title="Imprimir Relatório"
                className="flex items-center justify-center w-10 h-10 rounded-lg bg-white/10 hover:bg-white/20 text-brand-ice border border-brand-ice/20 shadow-md hover:shadow-lg transition-all duration-200 active:scale-[0.97] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Printer className="w-4 h-4" />
              </button>
            )}
            <button
              data-testid="auditoria-modal-close"
              onClick={() => onOpenChange(false)}
              className="p-2 rounded-lg text-brand-ice/60 hover:text-brand-ice hover:bg-white/5 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Filtros - apenas modo relatório */}
        {modo === "relatorio" && (
          <div className="px-6 py-4 border-b border-brand-ice/10 bg-white/[0.02]">
            <div className="flex items-center gap-2 mb-3">
              <Filter className="w-4 h-4 text-brand-ice/60" />
              <span className="text-brand-ice/70 text-xs uppercase tracking-wider font-bold">Filtros</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-brand-ice/60 font-bold">Data Inicial</Label>
                <Input
                  data-testid="audit-data-inicio"
                  type="date"
                  value={dataInicio}
                  onChange={(e) => setDataInicio(e.target.value)}
                  className="bg-white/10 border-brand-ice/20 text-brand-ice focus:border-brand-light mt-1"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-brand-ice/60 font-bold">Data Final</Label>
                <Input
                  data-testid="audit-data-fim"
                  type="date"
                  value={dataFim}
                  onChange={(e) => setDataFim(e.target.value)}
                  className="bg-white/10 border-brand-ice/20 text-brand-ice focus:border-brand-light mt-1"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-brand-ice/60 font-bold">Operador</Label>
                <div data-testid="audit-operador">
                  <BraveSelect
                    value={operadorCodigo}
                    onValueChange={setOperadorCodigo}
                    options={operadorOptions}
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-brand-ice/60 font-bold">Tipo de Operação</Label>
                <div data-testid="audit-operacao">
                  <BraveSelect
                    value={operacao}
                    onValueChange={setOperacao}
                    options={opOptions}
                    className="mt-1"
                  />
                </div>
              </div>
              <div className="flex items-end">
                <button
                  data-testid="audit-refresh"
                  onClick={fetchData}
                  className="flex items-center justify-center gap-2 w-full bg-brand-medium hover:bg-brand-dark text-white px-4 py-2 rounded-lg font-heading font-bold text-sm shadow-md transition-all duration-200 active:scale-[0.97]"
                >
                  <RotateCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                  Aplicar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-7 h-7 border-2 border-brand-light border-t-transparent rounded-full animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-16">
              <History className="w-12 h-12 text-brand-ice/30 mx-auto mb-3" />
              <p className="text-brand-ice/50 text-sm">Nenhum registro de histórico encontrado</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-white/5 border-b border-brand-ice/20">
                    <th className="w-8" />
                    <th className="text-left py-2.5 px-3 text-xs uppercase tracking-wider font-heading font-bold text-brand-ice/80">Data/Hora</th>
                    <th className="text-left py-2.5 px-3 text-xs uppercase tracking-wider font-heading font-bold text-brand-ice/80">Operação</th>
                    <th className="text-left py-2.5 px-3 text-xs uppercase tracking-wider font-heading font-bold text-brand-ice/80">Operador</th>
                    {modo === "relatorio" && (
                      <>
                        <th className="text-left py-2.5 px-3 text-xs uppercase tracking-wider font-heading font-bold text-brand-ice/80">Matrícula</th>
                        <th className="text-left py-2.5 px-3 text-xs uppercase tracking-wider font-heading font-bold text-brand-ice/80">Nome</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {items.map((i, idx) => (
                    <AuditRow key={i.id || idx} item={i} idx={idx} modo={modo} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-brand-ice/10 flex items-center justify-between text-xs text-brand-ice/60">
          <span>Total: <span className="text-brand-ice font-bold">{items.length}</span></span>
          <button
            onClick={() => onOpenChange(false)}
            className="px-5 py-2 rounded-lg text-brand-ice/70 bg-white/5 border border-brand-ice/20 hover:bg-white/10 hover:text-brand-ice transition-colors"
          >
            Fechar
          </button>
        </div>
      </div>

      {/* Modal de Impressão (modo relatório) */}
      {modo === "relatorio" && (
        <ImprimirListaModal
          open={imprimirOpen}
          onOpenChange={setImprimirOpen}
          titulo={tituloFinal}
          subtitulo={`Período: ${dataInicio || "—"} a ${dataFim || "—"}`}
          colunas={buildColunasAuditoria()}
          allRows={items}
          filteredRows={items}
          searchLabel={[
            operadorCodigo !== "" ? `Operador: ${(operadores.find(o => String(o.codigo) === String(operadorCodigo)) || {}).nome || operadorCodigo}` : "",
            operacao ? `Operação: ${({incluir: "Inclusão", alterar: "Alteração", excluir: "Exclusão"})[operacao] || operacao}` : "",
          ].filter(Boolean).join(" · ")}
        />
      )}
    </div>
  );
}

// Colunas do relatório impresso da Auditoria (modo relatório)
function buildColunasAuditoria() {
  return [
    {
      key: "data_hora_local",
      label: "Data/Hora",
      width: "34mm",
      formatter: (i) => {
        const v = i.data_hora_local;
        if (!v) return "";
        try {
          const [d, t] = v.split("T");
          const [y, mo, dd] = d.split("-");
          return `${dd}/${mo}/${y} ${(t || "").slice(0, 8)}`;
        } catch {
          return v;
        }
      },
    },
    {
      key: "operacao",
      label: "Operação",
      width: "22mm",
      formatter: (i) => ({ incluir: "Inclusão", alterar: "Alteração", excluir: "Exclusão" })[i.operacao] || (i.operacao || ""),
    },
    {
      key: "operador",
      label: "Operador",
      width: "52mm",
      formatter: (i) => {
        const cod = i.operador_codigo ?? "";
        const nome = i.operador_nome || "";
        return cod !== "" ? `${cod} - ${nome}` : nome;
      },
    },
    { key: "matricula", label: "Matrícula", align: "right", width: "22mm", formatter: (i) => i.matricula ?? "" },
    { key: "nome", label: "Nome", formatter: (i) => i.nome || "" },
    {
      key: "mudancas",
      label: "Mudanças",
      width: "22mm",
      align: "right",
      formatter: (i) => {
        const m = i?.extra?.mudancas || {};
        const n = Object.keys(m).length;
        return n > 0 ? `${n} ${n === 1 ? "campo" : "campos"}` : "";
      },
    },
  ];
}
